import { supabase } from "../config/supabase";

/**
 * Types for Digital Meeting Minutes (Ata Digital)
 */

export type TipoAta = "ordinaria" | "extraordinaria" | "instalacao_posse";
export type StatusAta = "rascunho" | "finalizada" | "assinada";
export type TipoAssinatura =
  | "efetivo"
  | "suplente"
  | "presidente"
  | "secretario"
  | "convidado";
export type StatusPresenca = "presente" | "ausente" | "justificado";

export interface Assinatura {
  id: string;
  nome: string;
  cargo: string;
  tipo: TipoAssinatura;
  assinado: boolean;
  data_assinatura?: string;
  ip_assinatura?: string;
  user_agent?: string;
}

export interface Deliberacao {
  id: string;
  descricao: string;
  responsavel: string;
  prazo?: string;
  status: "pendente" | "em_andamento" | "concluida";
}

export interface MembroPresenca {
  id: string;
  nome: string;
  cargo: string;
  tipo: TipoAssinatura;
  status: StatusPresenca;
  justificativa?: string;
}

export interface Ata {
  id?: string;
  empresa_id: string;
  usuario_id: string;
  tipo: TipoAta;
  numero_ata?: number;
  data_reuniao: string;
  hora_inicio: string;
  hora_fim?: string;
  local: string;
  pauta: string[];
  deliberacoes: Deliberacao[];
  assinaturas: Assinatura[];
  presencas: MembroPresenca[];
  motivo_extraordinaria?: string;
  observacoes?: string;
  status: StatusAta;
  created_at?: string;
  updated_at?: string;
}

export interface AtaFilters {
  empresa_id?: string;
  tipo?: TipoAta;
  status?: StatusAta;
  ano?: number;
  mes?: number;
}

/**
 * Service for managing Digital Meeting Minutes (Atas Digitais)
 */
export const ataService = {
  /**
   * Cria uma nova ata digital.
   * Valida campos obrigatórios e regras de atas extraordinárias.
   * Gera o número sequencial da ata por empresa/ano.
   * @param ata - Dados da ata (sem id/created_at/updated_at/numero_ata).
   * @returns Ata persistida com dados normalizados.
   * @throws Erro de validação ou falha na persistência.
   */
  async criar(
    ata: Omit<Ata, "id" | "created_at" | "updated_at" | "numero_ata">,
  ): Promise<Ata> {
    try {
      // Validate required fields
      if (!ata.empresa_id) throw new Error("ID da empresa é obrigatório");
      if (!ata.usuario_id) throw new Error("ID do usuário é obrigatório");
      if (!ata.data_reuniao) throw new Error("Data da reunião é obrigatória");
      if (!ata.local) throw new Error("Local da reunião é obrigatório");

      // Validate extraordinary meeting requires justification
      if (ata.tipo === "extraordinaria" && !ata.motivo_extraordinaria) {
        throw new Error("Motivo é obrigatório para atas extraordinárias");
      }

      // Check for existing installation meeting (only one per year)
      if (ata.tipo === "instalacao_posse") {
        const anoAtual = new Date(ata.data_reuniao).getFullYear();
        const { data: existingInstallation, error: checkError } = await supabase
          .from("cp_atas")
          .select("id")
          .eq("empresa_id", ata.empresa_id)
          .eq("tipo", "instalacao_posse")
          .gte("data_reuniao", `${anoAtual}-01-01`)
          .lte("data_reuniao", `${anoAtual}-12-31`)
          .single();

        if (existingInstallation && !checkError) {
          throw new Error(
            "Já existe uma ata de instalação e posse para este ano. Cada gestão pode ter apenas uma.",
          );
        }
      }

      // Get next ata number for this company and year
      const anoReuniao = new Date(ata.data_reuniao).getFullYear();
      const { data: lastAta } = await supabase
        .from("cp_atas")
        .select("numero_ata")
        .eq("empresa_id", ata.empresa_id)
        .gte("data_reuniao", `${anoReuniao}-01-01`)
        .lte("data_reuniao", `${anoReuniao}-12-31`)
        .order("numero_ata", { ascending: false })
        .limit(1)
        .single();

      const numeroAta = lastAta?.numero_ata ? lastAta.numero_ata + 1 : 1;

      // Prepare data for insertion
      const ataData = {
        empresa_id: ata.empresa_id,
        usuario_id: ata.usuario_id,
        tipo: ata.tipo,
        numero_ata: numeroAta,
        data_reuniao: ata.data_reuniao,
        hora_inicio: ata.hora_inicio,
        hora_fim: ata.hora_fim,
        local: ata.local,
        pauta: ata.pauta,
        deliberacoes: JSON.stringify(ata.deliberacoes),
        assinaturas: JSON.stringify(ata.assinaturas),
        presencas: JSON.stringify(ata.presencas),
        motivo_extraordinaria: ata.motivo_extraordinaria,
        observacoes: ata.observacoes,
        status: ata.status || "rascunho",
      };

      const { data, error } = await supabase
        .from("cp_atas")
        .insert(ataData)
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao criar ata:", error);
        throw error;
      }

      console.log("✅ Ata criada com sucesso:", data);
      return this.parseAtaFromDB(data);
    } catch (error) {
      console.error("❌ Erro no serviço de atas:", error);
      throw error;
    }
  },

  /**
   * Atualiza uma ata existente.
   * Revalida regras para atas extraordinárias e aplica somente campos enviados.
   * @param id - ID da ata.
   * @param updates - Campos a atualizar.
   * @returns Ata atualizada.
   * @throws Erro de validação ou falha na persistência.
   */
  async atualizar(id: string, updates: Partial<Ata>): Promise<Ata> {
    try {
      // Validate extraordinary meeting requires justification
      if (updates.tipo === "extraordinaria" && !updates.motivo_extraordinaria) {
        throw new Error("Motivo é obrigatório para atas extraordinárias");
      }

      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Only include fields that are being updated
      if (updates.tipo !== undefined) updateData.tipo = updates.tipo;
      if (updates.data_reuniao !== undefined)
        updateData.data_reuniao = updates.data_reuniao;
      if (updates.hora_inicio !== undefined)
        updateData.hora_inicio = updates.hora_inicio;
      if (updates.hora_fim !== undefined)
        updateData.hora_fim = updates.hora_fim;
      if (updates.local !== undefined) updateData.local = updates.local;
      if (updates.pauta !== undefined) updateData.pauta = updates.pauta;
      if (updates.deliberacoes !== undefined)
        updateData.deliberacoes = JSON.stringify(updates.deliberacoes);
      if (updates.assinaturas !== undefined)
        updateData.assinaturas = JSON.stringify(updates.assinaturas);
      if (updates.presencas !== undefined)
        updateData.presencas = JSON.stringify(updates.presencas);
      if (updates.motivo_extraordinaria !== undefined)
        updateData.motivo_extraordinaria = updates.motivo_extraordinaria;
      if (updates.observacoes !== undefined)
        updateData.observacoes = updates.observacoes;
      if (updates.status !== undefined) updateData.status = updates.status;

      const { data, error } = await supabase
        .from("cp_atas")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao atualizar ata:", error);
        throw error;
      }

      console.log("✅ Ata atualizada com sucesso:", data);
      return this.parseAtaFromDB(data);
    } catch (error) {
      console.error("❌ Erro ao atualizar ata:", error);
      throw error;
    }
  },

  /**
   * Exclui uma ata (somente rascunhos podem ser excluídos).
   * @param id - ID da ata.
   * @throws Erro quando a ata não é rascunho ou falha na exclusão.
   */
  async excluir(id: string): Promise<void> {
    try {
      // Check if ata is a draft
      const { data: ata, error: checkError } = await supabase
        .from("cp_atas")
        .select("status")
        .eq("id", id)
        .single();

      if (checkError) throw checkError;
      if (ata.status !== "rascunho") {
        throw new Error("Apenas atas em rascunho podem ser excluídas");
      }

      const { error } = await supabase.from("cp_atas").delete().eq("id", id);

      if (error) {
        console.error("❌ Erro ao excluir ata:", error);
        throw error;
      }

      console.log("✅ Ata excluída com sucesso");
    } catch (error) {
      console.error("❌ Erro ao excluir ata:", error);
      throw error;
    }
  },

  /**
   * Obtém uma ata pelo ID.
   * @param id - ID da ata.
   * @returns Ata encontrada ou null quando inexistente.
   * @throws Erro quando a consulta falhar.
   */
  async obter(id: string): Promise<Ata | null> {
    try {
      const { data, error } = await supabase
        .from("cp_atas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        if (error.code === "PGRST116") return null;
        throw error;
      }

      return this.parseAtaFromDB(data);
    } catch (error) {
      console.error("❌ Erro ao obter ata:", error);
      throw error;
    }
  },

  /**
   * Lista atas com filtros opcionais.
   * @param filters - Filtros por empresa, tipo, status, ano e mês.
   * @returns Lista de atas ordenadas por data.
   * @throws Erro quando a consulta falhar.
   */
  async listar(filters: AtaFilters = {}): Promise<Ata[]> {
    try {
      let query = supabase
        .from("cp_atas")
        .select("*")
        .order("data_reuniao", { ascending: false });

      if (filters.empresa_id) {
        query = query.eq("empresa_id", filters.empresa_id);
      }
      if (filters.tipo) {
        query = query.eq("tipo", filters.tipo);
      }
      if (filters.status) {
        query = query.eq("status", filters.status);
      }
      if (filters.ano) {
        query = query
          .gte("data_reuniao", `${filters.ano}-01-01`)
          .lte("data_reuniao", `${filters.ano}-12-31`);
      }
      if (filters.mes && filters.ano) {
        const mesStr = String(filters.mes).padStart(2, "0");
        const ultimoDia = new Date(filters.ano, filters.mes, 0).getDate();
        query = query
          .gte("data_reuniao", `${filters.ano}-${mesStr}-01`)
          .lte("data_reuniao", `${filters.ano}-${mesStr}-${ultimoDia}`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("❌ Erro ao listar atas:", error);
        throw error;
      }

      return (data || []).map(this.parseAtaFromDB);
    } catch (error) {
      console.error("❌ Erro ao listar atas:", error);
      throw error;
    }
  },

  /**
   * Registra assinatura em uma ata.
   * Atualiza status para "assinada" quando todas as assinaturas foram concluídas.
   * @param ataId - ID da ata.
   * @param assinaturaId - ID da assinatura a marcar.
   * @param ip - IP de origem (opcional).
   * @param userAgent - User-Agent do navegador (opcional).
   * @returns Ata atualizada.
   * @throws Erro quando a ata não existir ou na atualização.
   */
  async registrarAssinatura(
    ataId: string,
    assinaturaId: string,
    ip?: string,
    userAgent?: string,
  ): Promise<Ata> {
    try {
      // Get current ata
      const ata = await this.obter(ataId);
      if (!ata) throw new Error("Ata não encontrada");

      // Find and update the signature
      const assinaturas = ata.assinaturas.map((ass) => {
        if (ass.id === assinaturaId) {
          return {
            ...ass,
            assinado: true,
            data_assinatura: new Date().toISOString(),
            ip_assinatura: ip,
            user_agent: userAgent,
          };
        }
        return ass;
      });

      // Check if all signatures are complete
      const todasAssinadas = assinaturas.every((ass) => ass.assinado);
      const novoStatus: StatusAta = todasAssinadas ? "assinada" : ata.status;

      return await this.atualizar(ataId, {
        assinaturas,
        status: novoStatus,
      });
    } catch (error) {
      console.error("❌ Erro ao registrar assinatura:", error);
      throw error;
    }
  },

  /**
   * Finaliza uma ata (altera status de rascunho para finalizada).
   * Valida presença de pauta e assinaturas antes de concluir.
   * @param ataId - ID da ata.
   * @returns Ata finalizada.
   * @throws Erro de validação ou falha na atualização.
   */
  async finalizar(ataId: string): Promise<Ata> {
    try {
      const ata = await this.obter(ataId);
      if (!ata) throw new Error("Ata não encontrada");

      // Validate required fields for finalization
      if (!ata.pauta || ata.pauta.length === 0) {
        throw new Error("A pauta não pode estar vazia para finalizar a ata");
      }
      if (!ata.assinaturas || ata.assinaturas.length === 0) {
        throw new Error("Adicione pelo menos um membro para assinar a ata");
      }

      return await this.atualizar(ataId, { status: "finalizada" });
    } catch (error) {
      console.error("❌ Erro ao finalizar ata:", error);
      throw error;
    }
  },

  /**
   * Obtém estatísticas de atas por empresa e ano.
   * @param empresaId - ID da empresa.
   * @param ano - Ano de referência (opcional; padrão: ano atual).
   * @returns Totais por tipo e status.
   * @throws Erro quando a consulta falhar.
   */
  async obterEstatisticas(
    empresaId: string,
    ano?: number,
  ): Promise<{
    total: number;
    ordinarias: number;
    extraordinarias: number;
    instalacao: number;
    rascunhos: number;
    finalizadas: number;
    assinadas: number;
  }> {
    try {
      const anoFiltro = ano || new Date().getFullYear();

      const { data, error } = await supabase
        .from("cp_atas")
        .select("tipo, status")
        .eq("empresa_id", empresaId)
        .gte("data_reuniao", `${anoFiltro}-01-01`)
        .lte("data_reuniao", `${anoFiltro}-12-31`);

      if (error) throw error;

      const atas = data || [];

      return {
        total: atas.length,
        ordinarias: atas.filter((a) => a.tipo === "ordinaria").length,
        extraordinarias: atas.filter((a) => a.tipo === "extraordinaria").length,
        instalacao: atas.filter((a) => a.tipo === "instalacao_posse").length,
        rascunhos: atas.filter((a) => a.status === "rascunho").length,
        finalizadas: atas.filter((a) => a.status === "finalizada").length,
        assinadas: atas.filter((a) => a.status === "assinada").length,
      };
    } catch (error) {
      console.error("❌ Erro ao obter estatísticas:", error);
      throw error;
    }
  },

  /**
   * Converte uma linha do banco em `Ata`, normalizando campos JSON.
   * @param row - Registro vindo do banco.
   * @returns Ata com arrays normalizados.
   */
  parseAtaFromDB(row: any): Ata {
    return {
      id: row.id,
      empresa_id: row.empresa_id,
      usuario_id: row.usuario_id,
      tipo: row.tipo,
      numero_ata: row.numero_ata,
      data_reuniao: row.data_reuniao,
      hora_inicio: row.hora_inicio,
      hora_fim: row.hora_fim,
      local: row.local,
      pauta: row.pauta || [],
      deliberacoes:
        typeof row.deliberacoes === "string"
          ? JSON.parse(row.deliberacoes)
          : row.deliberacoes || [],
      assinaturas:
        typeof row.assinaturas === "string"
          ? JSON.parse(row.assinaturas)
          : row.assinaturas || [],
      presencas:
        typeof row.presencas === "string"
          ? JSON.parse(row.presencas)
          : row.presencas || [],
      motivo_extraordinaria: row.motivo_extraordinaria,
      observacoes: row.observacoes,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  },

  /**
   * Gera um identificador simples para itens locais.
   * @returns String única com timestamp + random.
   */
  generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Cria atas automaticamente a partir de reuniões agendadas.
   * Cada reunião gera uma ata em rascunho preenchida com informações da reunião.
   * @param reunioes - Reuniões para as quais criar atas.
   * @param empresaId - ID da empresa.
   * @param usuarioId - ID do usuário criador.
   * @returns Array de atas criadas.
   */
  async criarAtasParaReunioes(
    reunioes: Array<{
      id?: string;
      tipo: "ordinaria" | "extraordinaria";
      data_prevista: string;
      horario_inicio: string;
      horario_fim?: string | null;
      local: string;
      pauta?: string | null;
      motivo_extraordinaria?: string;
    }>,
    empresaId: string,
    usuarioId: string,
  ): Promise<Ata[]> {
    try {
      console.log(`📝 Criando ${reunioes.length} atas para reuniões...`);

      const atasCriadas: Ata[] = [];

      for (const reuniao of reunioes) {
        // Preparar dados da ata baseados na reunião
        const ataData: Omit<
          Ata,
          "id" | "created_at" | "updated_at" | "numero_ata"
        > = {
          empresa_id: empresaId,
          usuario_id: usuarioId,
          tipo: reuniao.tipo as TipoAta,
          data_reuniao: reuniao.data_prevista,
          hora_inicio: reuniao.horario_inicio,
          hora_fim: reuniao.horario_fim || undefined,
          local: reuniao.local,
          pauta: reuniao.pauta ? [reuniao.pauta] : [],
          deliberacoes: [],
          assinaturas: [],
          presencas: [],
          motivo_extraordinaria: (reuniao as any).motivo_extraordinaria,
          observacoes: `Ata criada automaticamente para reunião ${reuniao.tipo}`,
          status: "rascunho",
        };

        // Criar a ata
        const ataCriada = await this.criar(ataData);
        atasCriadas.push(ataCriada);

        // Vincular reunião com ata se reunião tem ID
        if (reuniao.id && ataCriada.id) {
          await supabase
            .from("cp_reunioes_agendadas")
            .update({ ata_id: ataCriada.id })
            .eq("id", reuniao.id);
        }
      }

      console.log(`✅ ${atasCriadas.length} atas criadas com sucesso!`);
      return atasCriadas;
    } catch (error) {
      console.error("❌ Erro ao criar atas para reuniões:", error);
      throw error;
    }
  },
};

export default ataService;
